/**
 * Product Info — iPad Settings card with core product fields.
 * Channel-aware: shows different fields for TikTok vs Lazada.
 *
 * TikTok: Title, Video URL are inline-editable.
 * Category opens a drill-down picker sheet (Epic 04).
 * SKU, SKU ID are always read-only. Brand is read-only (Epic 05).
 */

import { useState } from "react";
import { ExternalLink, ChevronRight } from "lucide-react";
import type { ExperienceWidgetProps } from "@/lib/views/widgets/registry";
import { getChannelConfig, formatCategoryChain, Divider, SettingsRow } from "./shared";
import { EditableSettingsRow } from "./EditableSettingsRow";
import { CategoryPickerSheet } from "./CategoryPickerSheet";
import { BrandSearchDropdown } from "./BrandSearchDropdown";
import {
  useTikTokEditStore,
  selectFieldValue,
  selectIsFieldDirty,
  selectIsReadOnly,
} from "../stores/useTikTokEditStore";

export function MarketplaceProductInfoWidget({ section, formData }: ExperienceWidgetProps) {
  const channel = section.description || "";
  const config = getChannelConfig(channel);
  const isTikTok = channel === "TikTok Shop";

  const title = formData?.[config.title] as string | undefined;
  const sku = formData?.[config.sku] as string | undefined;
  const skuId = formData?.[config.skuId] as string | undefined;
  const category = formData?.[config.category];
  const brand = formData?.[config.brandName] as string | undefined;
  const videoUrl = config.videoUrl ? (formData?.[config.videoUrl] as string | undefined) : null;
  const shopSku = config.shopSku ? (formData?.[config.shopSku] as string | undefined) : null;
  const listingUrl = config.listingUrl ? (formData?.[config.listingUrl] as string | undefined) : null;
  const warrantyType = config.warrantyType ? (formData?.[config.warrantyType] as string | undefined) : null;

  // ── TikTok: editable rows ─────────────────────────────────────

  if (isTikTok) {
    return (
      <TikTokProductInfo
        title={title}
        sku={sku}
        skuId={skuId}
        category={category}
        brand={brand}
        videoUrl={videoUrl}
        formData={formData}
      />
    );
  }

  // ── Lazada: read-only rows (unchanged) ────────────────────────

  const categoryDisplay = (category as string) || "\u2014";

  const rows: Array<{ label: string; value: React.ReactNode }> = [
    { label: "Title", value: title },
    { label: "Seller SKU", value: sku },
    { label: "Item ID", value: skuId },
  ];

  if (shopSku) {
    rows.push({ label: "Shop SKU", value: shopSku });
  }

  rows.push(
    { label: "Category", value: categoryDisplay },
    { label: "Brand", value: brand },
  );

  if (listingUrl) {
    rows.push({
      label: "Listing URL",
      value: (
        <a
          href={listingUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-primary hover:underline"
        >
          <span className="truncate max-w-[180px]">{listingUrl}</span>
          <ExternalLink className="h-3.5 w-3.5 shrink-0" />
        </a>
      ),
    });
  }

  if (warrantyType) {
    rows.push({ label: "Warranty", value: warrantyType });
  }

  return (
    <div className="space-y-2">
      <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide pl-4 mb-2">
        Product Info
      </h3>
      <div className="rounded-2xl overflow-hidden bg-card">
        {rows.map((row, i) => (
          <div key={row.label}>
            {i > 0 && <Divider />}
            <SettingsRow label={row.label} value={row.value} />
          </div>
        ))}
      </div>
    </div>
  );
}

// ── TikTok-specific section (extracted for hooks) ─────────────────

function TikTokProductInfo({
  title,
  sku,
  skuId,
  category,
  brand,
  videoUrl,
  formData: _formData,
}: {
  title: string | undefined;
  sku: string | undefined;
  skuId: string | undefined;
  category: unknown;
  brand: string | undefined;
  videoUrl: string | null | undefined;
  formData: Record<string, any>;
}) {
  const [pickerOpen, setPickerOpen] = useState(false);

  const connectionName = useTikTokEditStore((s) => s.connectionName);
  const setField = useTikTokEditStore((s) => s.setField);
  const clearAllValidationErrors = useTikTokEditStore((s) => s.clearAllValidationErrors);

  // Category display: use dirty chain if exists, else formData
  const dirtyCategoryChains = useTikTokEditStore(selectFieldValue("_category_chains"));
  const isCategoryDirty = useTikTokEditStore(selectIsFieldDirty("category_id"));
  const categoryDisplay = dirtyCategoryChains
    ? formatCategoryChain(dirtyCategoryChains)
    : formatCategoryChain(category);

  const isReadOnly = useTikTokEditStore(selectIsReadOnly);

  // Brand: read from dirty store or formData
  const currentCategoryId = useTikTokEditStore(selectFieldValue("category_id")) as string || "";
  const dirtyBrandName = useTikTokEditStore(selectFieldValue("brand_name")) as string | undefined;
  const brandDisplay = dirtyBrandName || brand;

  return (
    <div className="space-y-2">
      <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide pl-4 mb-2">
        Product Info
      </h3>
      <div className="rounded-2xl overflow-hidden bg-card">
        {/* Title — editable */}
        <EditableSettingsRow
          label="Title"
          value={title}
          fieldKey="title"
          editType="text"
          maxLength={255}
          minLength={25}
          placeholder="Enter product title"
        />
        <Divider />

        {/* SKU — read-only */}
        <EditableSettingsRow
          label="Seller SKU"
          value={sku}
          fieldKey="seller_sku"
          editType="readonly"
        />
        <Divider />

        {/* SKU ID — read-only */}
        <EditableSettingsRow
          label="SKU ID"
          value={skuId}
          fieldKey="sku_id"
          editType="readonly"
        />
        <Divider />

        {/* Category — tappable (or read-only if frozen) */}
        {isReadOnly ? (
          <div className="flex items-center gap-4 px-4 py-5 opacity-60">
            <span
              className="text-lg w-1/2"
              style={{ color: "rgb(17 24 39 / var(--tw-text-opacity, 1))" }}
            >
              Category
            </span>
            <div className="text-lg w-1/2 text-right text-foreground">
              <span className="break-words whitespace-pre-wrap">{categoryDisplay}</span>
            </div>
          </div>
        ) : (
          <button
            type="button"
            className="flex items-center gap-4 px-4 py-5 w-full text-left hover:bg-accent/50 transition-colors"
            onClick={() => setPickerOpen(true)}
          >
            <span
              className="text-lg w-1/2"
              style={{ color: "rgb(17 24 39 / var(--tw-text-opacity, 1))" }}
            >
              Category
            </span>
            <div className="text-lg w-1/2 text-right text-foreground flex items-center justify-end gap-1">
              <span className={`break-words whitespace-pre-wrap ${isCategoryDirty ? "font-medium" : ""}`}>
                {categoryDisplay}
              </span>
              <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
            </div>
          </button>
        )}
        <Divider />

        {/* Brand — search dropdown (Epic 05), static when read-only */}
        {connectionName && !isReadOnly && (
          <BrandSearchDropdown
            connectionName={connectionName}
            categoryId={currentCategoryId}
            currentBrandName={brandDisplay}
            onSelect={(brandId, brandName) => {
              setField("brand_id", brandId);
              setField("brand_name", brandName);
            }}
          />
        )}
        {(!connectionName || isReadOnly) && <SettingsRow label="Brand" value={brandDisplay} />}

        {/* Video URL — always show for TikTok, editable */}
        <Divider />
        <EditableSettingsRow
          label="Video"
          value={videoUrl}
          fieldKey="video_url"
          editType="text"
          placeholder="Add video URL"
          displayValue={
            videoUrl ? (
              <a
                href={videoUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-primary hover:underline"
                onClick={(e) => e.stopPropagation()}
              >
                <span className="truncate max-w-[180px]">{videoUrl}</span>
                <ExternalLink className="h-3.5 w-3.5 shrink-0" />
              </a>
            ) : undefined
          }
        />
      </div>

      {/* Category picker sheet */}
      {connectionName && (
        <CategoryPickerSheet
          open={pickerOpen}
          onOpenChange={setPickerOpen}
          connectionName={connectionName}
          onSelect={({ categoryId, categoryChain }) => {
            setField("category_id", categoryId);
            setField("_category_chains", categoryChain);
            // Clear validation errors — new category means new attributes
            clearAllValidationErrors();
          }}
        />
      )}
    </div>
  );
}
